import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  padding: 2rem;
`;

const LoginCard = styled.div`
  background-color: ${props => props.theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
`;

const LoginTitle = styled.h2`
  color: ${props => props.theme.colors.text};
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.primary};
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.text};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: ${props => props.theme.borderRadius.base};
  background: ${props => props.theme.colors.surface};
  font-family: ${props => props.theme.fonts.body};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Button = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.background};
  border: none;
  border-radius: ${props => props.theme.borderRadius.base};
  padding: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    opacity: 0.9;
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.divider};
    cursor: not-allowed;
  }
`;

const AdminButton = styled(Button)`
  background-color: ${props => props.theme.colors.accent};
  margin-top: 1rem;
  
  &:hover {
    background-color: #e55a2b;
    opacity: 1;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error || '#ff4444'};
  margin-bottom: 1rem;
  text-align: center;
  font-size: 0.9rem;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  
  &:before, &:after {
    content: "";
    flex: 1;
    border-bottom: 1px solid ${props => props.theme.colors.divider};
  }
  
  span {
    padding: 0 0.5rem;
    color: ${props => props.theme.colors.text};
    opacity: 0.6;
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  text-decoration: underline;
  margin-top: 1rem;
  
  &:hover {
    opacity: 0.8;
  }
`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  const [fullName, setFullName] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, signUp, adminLogin, error } = useAuth();
  const navigate = useNavigate();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isSignUp) {
        await signUp(email, password, { full_name: fullName });
        // After signup, user might need to verify email
        alert('Please check your email to verify your account.');
      } else if (isAdminLogin) {
        await adminLogin(email, password, adminPassword);
        navigate('/admin');
      } else {
        await login(email, password);
        navigate('/admin');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const getTitle = () => {
    if (isSignUp) return 'Create Account';
    if (isAdminLogin) return 'Admin Login';
    return 'Student Login';
  };
  
  return (
    <LoginContainer>
      <LoginCard>
        <LoginTitle>{getTitle()}</LoginTitle>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        }
        
        <LoginForm onSubmit={handleSubmit}>
          {isSignUp && (
            <FormGroup>
              <Label htmlFor="fullName">Full Name</Label>
              <Input 
                type="text" 
                id="fullName" 
                value={fullName} 
                onChange={(e) => setFullName(e.target.value)} 
                required 
              />
            </FormGroup>
          )}
          
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input 
              type="email" 
              id="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input 
              type="password" 
              id="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
            />
          </FormGroup>
          
          {isAdminLogin && (
            <FormGroup>
              <Label htmlFor="adminPassword">Admin Password</Label>
              <Input 
                type="password" 
                id="adminPassword" 
                value={adminPassword} 
                onChange={(e) => setAdminPassword(e.target.value)} 
                placeholder="Enter admin password"
                required 
              />
            </FormGroup>
          )}
          
          <Button type="submit" disabled={loading}>
            {loading ? 
              (isSignUp ? 'Creating Account...' : 'Logging in...') : 
              (isSignUp ? 'Create Account' : (isAdminLogin ? 'Admin Login' : 'Student Login'))
            }
          </Button>
        </LoginForm>
        
        {!isSignUp && (
          <>
            <Divider>
              <span>OR</span>
            </Divider>
            
            <AdminButton 
              type="button" 
              onClick={() => setIsAdminLogin(!isAdminLogin)} 
              disabled={loading}
            >
              {isAdminLogin ? 'Switch to Student Login' : 'Admin Login'}
            </AdminButton>
          </>
        )}
        
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <ToggleButton 
            onClick={() => setIsSignUp(!isSignUp)}
          >
            {isSignUp ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
          </ToggleButton>
        </div>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;